import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { siteImages } from '../data/mock';
import { Leaf, Shield, Award, Beaker, CheckCircle, ArrowRight } from 'lucide-react';

const IngredientsPage = () => {
  useEffect(() => {
    document.title = 'Our Ingredients | Keto Vybe';
  }, []);

  const ingredients = [
    {
      name: 'BHB Ketones',
      description: 'Beta-Hydroxybutyrate is an exogenous ketone that helps your body enter and maintain ketosis faster.',
      benefits: ['Supports ketosis', 'Boosts energy', 'Enhances mental clarity'],
      color: 'vybe'
    },
    {
      name: 'MCT Oil',
      description: 'Medium-chain triglycerides are rapidly absorbed fats that convert quickly to ketones for instant energy.',
      benefits: ['Instant energy', 'Supports fat burning', 'Easy to digest'],
      color: 'cyan'
    },
    {
      name: 'Electrolytes',
      description: 'Essential minerals including sodium, potassium, and magnesium to maintain hydration and prevent keto flu.',
      benefits: ['Prevents keto flu', 'Maintains hydration', 'Supports muscle function'],
      color: 'coral'
    },
    {
      name: 'Probiotics',
      description: 'Beneficial bacteria strains specifically chosen to support gut health on a high-fat, low-carb diet.',
      benefits: ['Supports digestion', 'Boosts immunity', 'Improves nutrient absorption'],
      color: 'vybe'
    },
    {
      name: 'Adaptogens',
      description: 'Natural herbs like ashwagandha and rhodiola that help your body manage stress and maintain balance.',
      benefits: ['Reduces stress', 'Supports energy', 'Improves focus'],
      color: 'cyan'
    },
    {
      name: 'Digestive Enzymes',
      description: 'Specialized enzymes including lipase to help break down fats and improve digestion on keto.',
      benefits: ['Aids fat digestion', 'Reduces bloating', 'Improves nutrient uptake'],
      color: 'coral'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-20">
        {/* Banner */}
        <section className="relative h-72 lg:h-80 overflow-hidden">
          <img 
            src={siteImages.featuredBackground}
            alt="Ingredients"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Quality First</span>
              <h1 className="font-display text-4xl lg:text-5xl text-white mb-3">
                Our Ingredients
              </h1>
              <p className="text-slate-400 max-w-md mx-auto">
                Premium, science-backed ingredients for optimal results
              </p>
            </div>
          </div>
        </section>

        {/* Quality Promise */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: Shield, title: 'Third-Party Tested', desc: 'Every batch tested for purity and potency' },
                { icon: Leaf, title: 'Clean Ingredients', desc: 'No artificial fillers or harmful additives' },
                { icon: Award, title: 'GMP Certified', desc: 'Made in USA facilities meeting highest standards' },
              ].map((item, i) => (
                <div key={i} className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 text-center">
                  <div className="w-14 h-14 bg-vybe-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-vybe-400" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key Ingredients */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-cyan-400 font-semibold text-sm tracking-wider uppercase mb-3 block">What's Inside</span>
              <h2 className="font-display text-3xl lg:text-4xl text-white mb-4">
                Key Ingredients We Use
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                Every ingredient is carefully selected for effectiveness and quality
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ingredients.map((ingredient, i) => (
                <div key={i} className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 hover:border-vybe-500/30 transition-all">
                  <div className={`w-12 h-12 bg-${ingredient.color}-600/20 rounded-xl flex items-center justify-center mb-4`}>
                    <Beaker className={`w-6 h-6 text-${ingredient.color}-400`} />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-2">{ingredient.name}</h3>
                  <p className="text-slate-400 text-sm mb-4">{ingredient.description}</p>
                  <ul className="space-y-2">
                    {ingredient.benefits.map((benefit, j) => (
                      <li key={j} className="flex items-center gap-2 text-slate-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-vybe-400 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Avoid */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-display text-2xl text-white mb-4">What We Never Use</h2>
              <p className="text-slate-400">Our products are free from harmful additives</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['Artificial Colors', 'Artificial Sweeteners', 'GMOs', 'Gluten', 'Soy', 'Dairy', 'Harmful Fillers', 'Preservatives'].map((item, i) => (
                <div key={i} className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700/50">
                  <span className="text-slate-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl text-white mb-4">Experience the Difference</h2>
            <p className="text-slate-400 mb-8">Quality ingredients make quality supplements.</p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white font-bold rounded-full hover:shadow-glow-purple transition-all"
            >
              Shop Products
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default IngredientsPage;
